/**
 * AI processing consent — App Review 5.1.2(i) (revised 16 Oct 2026):
 * personal data may be shared with a third-party AI only after clear
 * disclosure and the user's EXPLICIT permission. Before 17 Oct 2026 the first
 * "Organize My Week" sent task text (or a list photo) to the relay with no
 * consent step at all; the privacy policy explained it, but a policy page is
 * disclosure, not permission.
 *
 * Pure rules and the exact wording, so tests can pin both. The gate itself
 * has two layers (CLAUDE.md §1: never a single-hypothesis fix):
 *   1. the store refuses to start any AI action without consent and opens
 *      the consent sheet instead (user-visible layer);
 *   2. GeminiProvider refuses to build a request without consent
 *      (network layer — no future code path can bypass it).
 * Consent is device-local like `lastAccount`: persisted, never synced,
 * cleared by Erase. Absent = never asked = not granted (fail closed).
 */

export type AiConsent = 'granted' | 'declined';

/** Thrown by the network layer when a request is attempted without consent. */
export class ConsentRequiredError extends Error {
  constructor() {
    super('AI processing is off — turn it on in Settings → Privacy & Data to use AI sorting, plans and scans.');
    this.name = 'ConsentRequiredError';
  }
}

export function aiAllowed(consent: AiConsent | undefined): boolean {
  return consent === 'granted';
}

/** Which AI action was asked for while consent was missing — the sheet
 *  resumes it after "Agree". */
export type PendingAiAction =
  | { kind: 'sort'; rawInput: string }
  | { kind: 'drillDown'; taskId: string }
  | { kind: 'addExtraStep'; taskId: string }
  | { kind: 'updatePlan'; taskId: string; note: string };

/** The consent sheet, word for word (review notes quote it). */
export const AI_CONSENT_COPY = {
  title: 'How ZoneIn’s AI works',
  intro:
    'To sort your tasks, build action plans or read a photographed list, ZoneIn sends the text you enter — or the photo you take — to our own server on Google Cloud, where Google’s Gemini model processes it and sends the result straight back to this device.',
  points: [
    'Your content is not used to train AI models: Google Cloud’s terms don’t allow it without our permission, and we don’t give it.',
    'Our own server doesn’t store it. Google may hold a request briefly (up to 24 hours) and may log it to check for abuse of its service.',
    'It is encrypted in transit and is never sold or shared with anyone else.',
    'Nothing is sent until you tap an AI action — and nothing is sent at all without your agreement here.',
    'You can switch AI processing off at any time in Settings → Privacy & Data.',
  ],
  withoutAi:
    'You can use ZoneIn without AI: add tasks with Quick add, sort them on this device, and write your own steps.',
  agree: 'Agree and continue',
  notNow: 'Not now',
  sortOnDevice: 'Sort on this device instead',
  policy: 'Read the privacy policy',
} as const;

/** What the sheet says the pending action will send. */
export function pendingActionLabel(p: PendingAiAction | undefined): string {
  if (!p) return '';
  if (p.kind === 'sort') return 'Organize sends your brain dump — and any photos you attached — for sorting.';
  if (p.kind === 'updatePlan') return 'Updating a plan sends the task, its steps and your note.';
  return 'Building an action plan sends the task’s text.';
}
